import React, { useState } from 'react'
import styled from 'styled-components'
import { NavigateFunction } from 'react-router-dom'
import { pdf } from '@react-pdf/renderer'
import { Eye, Download, X } from 'lucide-react'
import ApiService from '../../../../services/ApiService'
import CertificatePDFDocument from '../../../../components/Certificates/CertificatePDFGenerator/PDFTemplate/index'
import { mapCertificateApiToPdfData } from '../../../../components/Certificates/utils/mapCertificateApiToPdfData'
import { OverflowCell } from '../../../../components/OverflowCell'
import { StandardColumn } from '../../../../components/StandardPage'
import { Stock } from '../../../../contexts/StockContext'
import CertificatesContext from '../../../../contexts/CertificatesContext'
import ModalContext from '../../../../contexts/ModalContext'
import { formatReceivedDate, formatStockNumericValue } from './utils'
import openSignedAttachment, { getSignedAttachmentUrl } from '../../../../utils/openSignedAttachment'

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #0090d3;
  text-decoration: none;
  cursor: pointer;
  font-weight: bold;
  padding: 0;
  font-size: inherit;
  font-family: inherit;
`

const CellLink = styled.a`
  text-decoration: none;
  text-align: center;
  color: #0090d3;
  font-weight: bold;
`

const ColorSwatchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: inherit;
`

const ColorSwatchCircle = styled.span<{ $hex?: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => {
    const hex = props.$hex;
    if (!hex) return '#bdbdbd';
    return hex.startsWith('#') ? hex : `#${hex}`;
  }};
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: inline-block;
  flex-shrink: 0;
`

const SwatchText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const numericStockFields = new Set([
  'dimension1',
  'dimension2',
  'density',
  'availableBalance',
  'totalCmc',
  'unitCmc',
  'lengthInMm',
  'markup',
])

const overflowTextFields = new Set(['code', 'material', 'description', 'characteristic', 'stockStatus'])

const getFirstWithEllipsis = (content: string) => {
  if (!content) return '-'
  const parts = content
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length === 0) return '-'
  if (parts.length === 1) return parts[0]
  return `${parts[0]}...`
}

const getQuantityUnitName = (qty: any) =>
  (qty.UnitOfMeasurement?.name || qty.unitOfMeasurement?.name || 'N/A').toUpperCase()

const formatQuantityEntry = (qty: any) =>
  `${formatStockNumericValue(qty.quantity)} ${getQuantityUnitName(qty)}`

const getQuantityCellDisplay = (quantities?: any[]) => {
  if (!quantities || quantities.length === 0) return 'N/A'
  const preferred = quantities.find((qty) => getQuantityUnitName(qty) === 'MM') ?? quantities[0]
  const formatted = formatQuantityEntry(preferred)
  return quantities.length > 1 ? `${formatted}...` : formatted
}

const formatQuantities = (quantities?: any[]) => {
  if (!quantities || quantities.length === 0) return 'N/A'
  return quantities.map(formatQuantityEntry).join('\n')
}

const formatPrices = (prices?: any[]) => {
  if (!prices || prices.length === 0) return 'N/A'
  return prices.map(price => {
    const formattedPrice = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parseFloat(price.price))
    return `${formattedPrice}/${price.UnitOfMeasurement?.name || price.unitOfMeasurement?.name || 'N/A'}`
  }).join('\n')
}

export const handleCheckCertificate = async (certificateId: number) => {
  try {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write('<div style="font-family: sans-serif; text-align: center; margin-top: 50px;">Gerando certificado, por favor aguarde...</div>');
    }

    const [response1, response2] = await Promise.all([
      ApiService.certificates.findById(Number(certificateId)),
      ApiService.certificates.getUtils(Number(certificateId))
    ]);

    const mappedData = mapCertificateApiToPdfData(
      response1.data,
      response2.data,
      Number(certificateId)
    );

    const blob = await pdf(<CertificatePDFDocument certificate={mappedData} />).toBlob();
    const url = URL.createObjectURL(blob);

    if (newWindow) {
      newWindow.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  } catch (error) {
    console.error('Erro ao gerar PDF do certificado', error);
    alert('Não foi possível gerar o certificado.');
  }
}

export const handleDownloadCertificate = async (certificateId: number, fileName?: string) => {
  try {
    const [response1, response2] = await Promise.all([
      ApiService.certificates.findById(Number(certificateId)),
      ApiService.certificates.getUtils(Number(certificateId))
    ]);

    const mappedData = mapCertificateApiToPdfData(
      response1.data,
      response2.data,
      Number(certificateId)
    );

    const blob = await pdf(<CertificatePDFDocument certificate={mappedData} />).toBlob();
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `Certificado_${certificateId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Erro ao baixar PDF do certificado', error);
    alert('Não foi possível baixar o certificado.');
  }
}

const SelectCertificateModal: React.FC<{
  item: Stock
  certificates: any[]
  onClose: () => void
}> = ({ item, certificates, onClose }) => {
  const { setCertificate } = React.useContext(CertificatesContext)
  const { setModalSection, setModalActive } = React.useContext(ModalContext)

  const sortedCertificates = React.useMemo(() => {
    return [...certificates].sort((a: any, b: any) => {
      const rawTypeA = String(a.type?.name || a.type || '').trim()
      const resolvedA = (rawTypeA === 'Origem / Qualidade' || rawTypeA === '1')
        ? (a.supplierIsMain ? 'Qualidade' : 'Origem')
        : rawTypeA
      const rawTypeB = String(b.type?.name || b.type || '').trim()
      const resolvedB = (rawTypeB === 'Origem / Qualidade' || rawTypeB === '1')
        ? (b.supplierIsMain ? 'Qualidade' : 'Origem')
        : rawTypeB

      const isOrigemA = resolvedA.toLowerCase().includes('origem') ? 0 : 1
      const isOrigemB = resolvedB.toLowerCase().includes('origem') ? 0 : 1
      return isOrigemA - isOrigemB
    })
  }, [certificates])

  const [selectedCertId, setSelectedCertId] = useState<number>(sortedCertificates[0]?.id || 0)

  const handleOpen = (cert: any) => {
    onClose()
    setCertificate((prev: any) => ({ ...prev, id: cert.id }))
    setModalSection('viewCertificate')
    setModalActive(true)
  }

  const handleDownload = async (cert: any) => {
    if (cert.supplierFile) {
      const fileName = `Certificado_${cert.type || cert.id}.pdf`
      const url = await getSignedAttachmentUrl(cert.supplierFile, true, fileName)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } else {
      handleDownloadCertificate(cert.id, `Certificado_${cert.type || cert.id}.pdf`)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '24px',
          width: '750px',
          maxWidth: '92vw',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
          textAlign: 'left',
          color: '#333',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111' }}>
            Certificados do Estoque
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: '4px', borderRadius: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: '16px', fontSize: '13px', color: '#555', backgroundColor: '#f8fafc', padding: '10px 14px', borderRadius: '6px' }}>
          <div><strong>Item:</strong> {(item as any).code || item.description || `Estoque #${item.id}`}</div>
          {(item as any).heatNumber && <div style={{ marginTop: '2px' }}><strong>Corrida:</strong> {(item as any).heatNumber}</div>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '380px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>
          {sortedCertificates.map((cert) => {
            const isSelected = cert.id === selectedCertId
            const rawType = String(cert.type?.name || cert.type || '').trim()
            let resolvedType = rawType
            if (rawType === 'Origem / Qualidade' || rawType === '1') {
              resolvedType = cert.supplierIsMain ? 'Qualidade' : 'Origem'
            }
            const certTypeDisplay = resolvedType
              ? (resolvedType.toLowerCase().startsWith('certificado') ? resolvedType : `Certificado de ${resolvedType}`)
              : `Certificado #${cert.id}`
            const normVal = cert.normName || cert.standard || cert.Param?.astm || cert.Param?.din || cert.Param?.aisi || cert.Param?.uns || cert.Param?.others || ''
            const paramVal = cert.paramName || cert.param || cert.Param?.name || ''

            return (
              <div
                key={cert.id}
                onClick={() => setSelectedCertId(cert.id)}
                style={{
                  border: isSelected ? '2px solid #0090d3' : '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  backgroundColor: isSelected ? '#f0f9ff' : '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxSizing: 'border-box',
                }}
              >
                <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>
                    {certTypeDisplay}
                  </div>
                  <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px', lineHeight: '1.4' }}>
                    {cert.supplierName && <div><strong>Fornecedor:</strong> {cert.supplierName}</div>}
                    {cert.customerName && <div><strong>Cliente:</strong> {cert.customerName}</div>}
                    {normVal && <div><strong>Norma:</strong> {normVal}</div>}
                    {paramVal && <div><strong>Parâmetro:</strong> {paramVal}</div>}
                    {cert.heatNumber && <div><strong>Corrida:</strong> {cert.heatNumber}</div>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleOpen(cert)
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '7px 12px',
                      borderRadius: '6px',
                      border: '1px solid #0090d3',
                      backgroundColor: '#0090d3',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    title="Visualizar Certificado"
                  >
                    <Eye size={15} />
                    Visualizar
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDownload(cert)
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '7px 12px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      color: '#374151',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                    title="Baixar Certificado"
                  >
                    <Download size={15} />
                    Baixar
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const CertificateCell: React.FC<{ item: Stock }> = ({ item }) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { setCertificate } = React.useContext(CertificatesContext)
  const { setModalSection, setModalActive } = React.useContext(ModalContext)

  if (item.externalCertificateUrl) {
    return (
      <CellLink
        href="#"
        onClick={(e) => {
          e.preventDefault()
          openSignedAttachment(item.externalCertificateUrl!)
        }}
      >
        Visualizar
      </CellLink>
    )
  }

  const itemAny = item as any
  const certs: any[] = itemAny.certificates || []
  const displayText = item.certificateNumber || item.certificateParam

  if (certs.length > 1) {
    return (
      <>
        <LinkButton type="button" onClick={() => setIsModalOpen(true)}>
          Visualizar
        </LinkButton>
        {isModalOpen && (
          <SelectCertificateModal
            item={item}
            certificates={certs}
            onClose={() => setIsModalOpen(false)}
          />
        )}
      </>
    )
  }

  if (certs.length === 1) {
    const cert = certs[0]
    return (
      <LinkButton
        type="button"
        onClick={() => {
          setCertificate((prev: any) => ({ ...prev, id: cert.id }))
          setModalSection('viewCertificate')
          setModalActive(true)
        }}
      >
        Visualizar
      </LinkButton>
    )
  }

  const systemCertId = item.certificateId || (item.HeatNumber?.Certificate && item.HeatNumber.Certificate.length > 0 ? item.HeatNumber.Certificate[0].id : null)

  if (systemCertId) {
    return (
      <LinkButton
        type="button"
        onClick={() => {
          setCertificate((prev: any) => ({ ...prev, id: Number(systemCertId) }))
          setModalSection('viewCertificate')
          setModalActive(true)
        }}
      >
        Visualizar
      </LinkButton>
    )
  }

  const certWithFile = item.HeatNumber?.Certificate?.find((c: any) => c.id != null)
  if (certWithFile && certWithFile.id) {
    return (
      <LinkButton
        type="button"
        onClick={() => {
          setCertificate((prev: any) => ({ ...prev, id: Number(certWithFile.id) }))
          setModalSection('viewCertificate')
          setModalActive(true)
        }}
      >
        Visualizar
      </LinkButton>
    )
  }

  return <span>{displayText || '-'}</span>
}

const InvoiceCell: React.FC<{ item: Stock; navigate: NavigateFunction }> = ({ item, navigate }) => {
  if (item.externalInvoiceUrl) {
    return (
      <CellLink
        href="#"
        onClick={(e) => {
          e.preventDefault()
          openSignedAttachment(item.externalInvoiceUrl!)
        }}
      >
        {item.nf || item.invoiceParam || 'Visualizar NF'}
      </CellLink>
    )
  }
  if (item.invoiceId) {
    return (
      <LinkButton type="button" onClick={() => navigate(`/panel/invoice/view/${item.invoiceId}`)}>
        {item.nf || item.invoiceParam || `NF #${item.invoiceId}`}
      </LinkButton>
    )
  }
  return <span>{item.nf || item.invoiceParam || '-'}</span>
}

const getStockFieldValue = (item: Stock, field: string): any => {
  if (field === 'stockStatus') return (item as any).stockStatus ?? (item as any).status ?? ''
  const raw = (item as any)[field]
  return (raw as any)?.name ?? raw
}

interface StockColumnDef {
  id: string
  label: string
  visible: boolean
  parentId?: string
}

interface StockQuantityColumn {
  key: string
  label: string
  names: string[]
}

interface StockPriceColumn {
  key: string
  label: string
  names: string[]
}

const stockQuantityColumns: StockQuantityColumn[] = [
  { key: 'un', label: 'Quantidade (un)', names: ['UN', 'UNIDADE', 'PC', 'PÇ'] },
  { key: 'kg', label: 'Peso total (kg)', names: ['KG'] },
  { key: 'mm', label: 'Comprimento (mm)', names: ['MM'] },
  { key: 'm2', label: 'Quantidade (m²)', names: ['M²', 'M2', 'METRO QUADRADO'] },
  { key: 'mlinear', label: 'Comprimento (m linear)', names: ['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'] },
]

const stockPriceColumns: StockPriceColumn[] = [
  { key: 'kg', label: 'Custo (kg)', names: ['KG'] },
  { key: 'mm', label: 'Custo (mm)', names: ['MM'] },
  { key: 'un', label: 'Custo (un)', names: ['UN', 'UNIDADE', 'PC', 'PÇ'] },
  { key: 'm2', label: 'Custo (m²)', names: ['M²', 'M2', 'METRO QUADRADO'] },
  { key: 'mlinear', label: 'Custo (m linear)', names: ['M LINEAR', 'LINEAR', 'METRO LINEAR', 'M'] },
]

const withUnitSubColumns = (defs: StockColumnDef[]): StockColumnDef[] =>
  defs.flatMap((def) => {
    if (def.id === 'quantities') {
      return [
        def,
        ...stockQuantityColumns.map((unit) => ({
          id: `quantities_${unit.key}`,
          label: unit.label,
          visible: false,
          parentId: 'quantities',
        })),
      ]
    }
    if (def.id === 'prices') {
      return [
        def,
        ...stockPriceColumns.map((unit) => ({
          id: `prices_${unit.key}`,
          label: unit.label,
          visible: false,
          parentId: 'prices',
        })),
      ]
    }
    return [def]
  })

const findQuantityEntryByUnit = (entries: any[] | undefined, unitKey: string) => {
  if (!entries || entries.length === 0) return undefined
  const unit = stockQuantityColumns.find((u) => u.key === unitKey)
  if (!unit) return undefined
  return entries.find((entry) => unit.names.includes(getQuantityUnitName(entry)))
}

const findPriceEntryByUnit = (entries: any[] | undefined, unitKey: string) => {
  if (!entries || entries.length === 0) return undefined
  const unit = stockPriceColumns.find((u) => u.key === unitKey)
  if (!unit) return undefined
  return entries.find((entry) => unit.names.includes(getQuantityUnitName(entry)))
}

const parseStockNumber = (value: unknown): number | '' => {
  if (value == null || value === '') return ''
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'))
  return isNaN(num) ? '' : num
}

const formatUnitPrice = (entry: any) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(parseFloat(entry.price))

const steelColumnDefs: StockColumnDef[] = [
  { id: 'isActivated', label: 'Disponível', visible: true },
  { id: 'code', label: 'Código', visible: true },
  { id: 'material', label: 'Material', visible: true },
  { id: 'color', label: 'Cor', visible: false },
  { id: 'format', label: 'Formato', visible: true },
  { id: 'finishing', label: 'Acabamento', visible: true },
  { id: 'fabrication', label: 'Fabricação', visible: true },
  { id: 'heatTreatment', label: 'Tratamento Térmico', visible: false },
  { id: 'originCountry', label: 'País de Origem', visible: false },
  { id: 'supplier', label: 'Fornecedor', visible: false },
  { id: 'stockStreet', label: 'Rua do Estoque', visible: false },
  { id: 'shelf', label: 'Prateleira', visible: false },
  { id: 'stockLocation', label: 'Localização no Estoque', visible: false },
  { id: 'ncm', label: 'NCM', visible: false },
  { id: 'quantities', label: 'Conjunto de Quantidades', visible: true },
  { id: 'prices', label: 'Conjunto de Preço', visible: true },
  { id: 'dimension1', label: 'Dimensão 1', visible: false },
  { id: 'dimension2', label: 'Dimensão 2', visible: false },
  { id: 'invoice', label: 'Nota Fiscal', visible: false },
  { id: 'density', label: 'Densidade', visible: false },
  { id: 'certificateUrl', label: 'Certificados', visible: false },
  { id: 'totalCmc', label: 'CMC total', visible: false },
  { id: 'availableBalance', label: 'Saldo Disponível', visible: false },
  { id: 'standardsMet', label: 'Normas Atendidas', visible: false },
  { id: 'markup', label: 'Markup', visible: false },
  { id: 'trackingNumber', label: 'Número de Rastreamento', visible: false },
  { id: 'receivedDate', label: 'Data de Recebimento', visible: false },
  { id: 'purchaseOrder', label: 'Ordem de Compra', visible: false },
  { id: 'heatNumber', label: 'Número da Corrida', visible: false },
  { id: 'family', label: 'Família', visible: false },
  { id: 'description', label: 'Descrição', visible: false },
  { id: 'characteristic', label: 'Característica', visible: false },
  { id: 'carrierCode', label: 'Código da transportadora', visible: false },
  { id: 'stockStatus', label: 'Status', visible: false },
]

const fashionColumnDefs: StockColumnDef[] = [
  { id: 'isActivated', label: 'Disponível', visible: true },
  { id: 'code', label: 'Código', visible: true },
  { id: 'material', label: 'Material', visible: true },
  { id: 'color', label: 'Cor', visible: true },
  { id: 'description', label: 'Descrição', visible: true },
  { id: 'quantities', label: 'Conjunto de Quantidades', visible: true },
  { id: 'prices', label: 'Conjunto de Preço', visible: true },
  { id: 'format', label: 'Formato', visible: false },
  { id: 'finishing', label: 'Acabamento', visible: false },
  { id: 'fabrication', label: 'Fabricação', visible: false },
  { id: 'originCountry', label: 'País de Origem', visible: false },
  { id: 'supplier', label: 'Fornecedor', visible: false },
  { id: 'stockStreet', label: 'Rua do Estoque', visible: false },
  { id: 'shelf', label: 'Prateleira', visible: false },
  { id: 'stockLocation', label: 'Localização no Estoque', visible: false },
  { id: 'ncm', label: 'NCM', visible: false },
  { id: 'dimension1', label: 'Dimensão 1', visible: false },
  { id: 'dimension2', label: 'Dimensão 2', visible: false },
  { id: 'invoice', label: 'Nota Fiscal', visible: false },
  { id: 'certificateUrl', label: 'Certificados', visible: false },
  { id: 'totalCmc', label: 'CMC total', visible: false },
  { id: 'availableBalance', label: 'Saldo Disponível', visible: false },
  { id: 'standardsMet', label: 'Normas Atendidas', visible: false },
  { id: 'markup', label: 'Markup', visible: false },
  { id: 'trackingNumber', label: 'Número de Rastreamento', visible: false },
  { id: 'receivedDate', label: 'Data de Recebimento', visible: false },
  { id: 'purchaseOrder', label: 'Ordem de Compra', visible: false },
  { id: 'heatNumber', label: 'Número da Corrida', visible: false },
  { id: 'family', label: 'Família', visible: false },
  { id: 'characteristic', label: 'Característica', visible: false },
  { id: 'carrierCode', label: 'Código da transportadora', visible: false },
  { id: 'stockStatus', label: 'Status', visible: false },
]

const columnBehavior = (id: string, navigate: NavigateFunction): Partial<StandardColumn<Stock>> => {
  if (id === 'certificateUrl') {
    return {
      sortable: false,
      accessor: (item) => item.externalCertificateUrl || item.certificateNumber || item.certificateParam || '',
      cell: (item) => <CertificateCell item={item} />,
      exportValue: (item) => item.externalCertificateUrl || item.certificateNumber || item.certificateParam || ((item as any).certificates?.length ? 'Visualizar' : ''),
    }
  }
  if (id === 'invoice') {
    return {
      sortable: false,
      accessor: (item) => item.nf || item.invoiceParam || '',
      cell: (item) => <InvoiceCell item={item} navigate={navigate} />,
    }
  }
  if (id === 'isActivated') {
    return {
      accessor: (item) => item.isActivated,
      cell: (item) => {
        const value = item.isActivated as any
        return <span>{value === true || value === 'true' || value === 1 ? 'Ativo' : 'Inativo'}</span>
      },
      exportValue: (item) => {
        const value = item.isActivated as any
        return value === true || value === 'true' || value === 1 ? 'Ativo' : 'Inativo'
      },
    }
  }
  if (id === 'quantities') {
    return {
      excludeFromExport: true,
      sortable: false,
      accessor: (item) => getQuantityCellDisplay(item.quantities),
      cell: (item) => (
        <OverflowCell
          text={formatQuantities(item.quantities)}
          forceTooltip={!!item.quantities && item.quantities.length > 1}
        >
          {getQuantityCellDisplay(item.quantities)}
        </OverflowCell>
      ),
      exportValue: (item) => formatQuantities(item.quantities),
    }
  }
  if (id.startsWith('quantities_')) {
    const unitKey = id.slice('quantities_'.length)
    return {
      sortable: true,
      accessor: (item) => {
        const entry = findQuantityEntryByUnit(item.quantities, unitKey)
        if (!entry || entry.quantity === null || entry.quantity === undefined) return -Infinity
        const val = typeof entry.quantity === 'number' ? entry.quantity : parseFloat(String(entry.quantity))
        return isNaN(val) ? -Infinity : val
      },
      cell: (item) => {
        const entry = findQuantityEntryByUnit(item.quantities, unitKey)
        return <>{entry ? formatStockNumericValue(entry.quantity) : '-'}</>
      },
      exportValue: (item) => {
        const entry = findQuantityEntryByUnit(item.quantities, unitKey)
        return entry ? parseStockNumber(entry.quantity) : ''
      },
    }
  }
  if (id.startsWith('prices_')) {
    const unitKey = id.slice('prices_'.length)
    return {
      sortable: true,
      accessor: (item) => {
        const entry = findPriceEntryByUnit(item.prices, unitKey)
        if (!entry || entry.price === null || entry.price === undefined) return -Infinity
        const val = typeof entry.price === 'number' ? entry.price : parseFloat(String(entry.price))
        return isNaN(val) ? -Infinity : val
      },
      cell: (item) => {
        const entry = findPriceEntryByUnit(item.prices, unitKey)
        return <>{entry ? formatUnitPrice(entry) : '-'}</>
      },
      exportValue: (item) => {
        const entry = findPriceEntryByUnit(item.prices, unitKey)
        return entry ? parseStockNumber(entry.price) : ''
      },
    }
  }
  if (id === 'prices') {
    return {
      excludeFromExport: true,
      sortable: false,
      accessor: (item) => getFirstWithEllipsis(formatPrices(item.prices)),
      cell: (item) => (
        <OverflowCell
          text={formatPrices(item.prices)}
          forceTooltip={!!item.prices && item.prices.length > 1}
        >
          {getFirstWithEllipsis(formatPrices(item.prices))}
        </OverflowCell>
      ),
      exportValue: (item) => formatPrices(item.prices),
    }
  }
  if (overflowTextFields.has(id)) {
    return {
      accessor: (item) => getStockFieldValue(item, id),
      cell: (item) => {
        const raw = getStockFieldValue(item, id)
        if (!raw) return <span>-</span>
        return <OverflowCell text={String(raw)} />
      },
      exportValue: (item) => {
        const raw = getStockFieldValue(item, id)
        return raw ? String(raw) : ''
      },
    }
  }
  if (id === 'receivedDate') {
    return {
      accessor: (item) => item.receivedDate,
      cell: (item) => <>{formatReceivedDate(item.receivedDate)}</>,
      exportValue: (item) => (item.receivedDate ? formatReceivedDate(item.receivedDate) : ''),
    }
  }
  if (id === 'color') {
    return {
      accessor: (item) => item.color,
      cell: (item) => {
        const raw = `${item.color as string}${item.pantoneCode ? ` (${item.pantoneCode})` : ''}`
        return (
          <OverflowCell text={raw}>
            <ColorSwatchContainer>
              <ColorSwatchCircle $hex={item.pantoneHex} />
              <SwatchText>{raw}</SwatchText>
            </ColorSwatchContainer>
          </OverflowCell>
        )
      },
      exportValue: (item) => `${item.color ?? ''}${item.pantoneCode ? ` (${item.pantoneCode})` : ''}`,
    }
  }
  if (numericStockFields.has(id)) {
    return {
      accessor: (item) => getStockFieldValue(item, id),
      cell: (item) => <>{formatStockNumericValue(getStockFieldValue(item, id))}</>,
      exportValue: (item) => parseStockNumber(getStockFieldValue(item, id)),
    }
  }
  return {
    accessor: (item) => getStockFieldValue(item, id),
    cell: (item) => {
      const raw = getStockFieldValue(item, id)
      return <>{raw ?? ''}</>
    },
    exportValue: (item) => {
      const raw = getStockFieldValue(item, id)
      return raw === null || raw === undefined ? '' : String(raw)
    },
  }
}

interface BuildStockColumnsOptions {
  isFashion: boolean
  navigate: NavigateFunction
}

export const stockScreenKey = (isFashion: boolean) => (isFashion ? 'stock_current_fashion' : 'stock_current_steel')

export const buildStockColumns = ({ isFashion, navigate }: BuildStockColumnsOptions): StandardColumn<Stock>[] =>
  withUnitSubColumns(isFashion ? fashionColumnDefs : steelColumnDefs).map((def) => ({
    id: def.id,
    label: def.label,
    parentId: def.parentId,
    defaultHidden: !def.visible,
    ...columnBehavior(def.id, navigate),
  }))
